const API = process.env.NEXT_PUBLIC_API_URL

export type RuleCard = {
  slug: string
  title: string
  short_title?: string
  issuer?: string
  category: 'official_national' | 'world' | 'provincial' | 'institutional' | 'event'
  domain?: 'card' | 'ball'
  game?: string
  year?: number
  cover?: string
  is_authoritative: boolean
  summary?: string
  views?: number
}

export type RuleDetail = RuleCard & {
  display_type: 'html_text' | 'image_pages' | 'file'
  version_label?: string
  source_url?: string
  body_html?: string
  cdn_base?: string
  page_count?: number
  file_url?: string
  file_kind?: 'pdf' | 'image'
  toc: string[]
  can_edit?: boolean
  body_md?: string
}

export type RuleEditPayload = {
  title?: string
  short_title?: string
  issuer?: string
  category?: RuleCard['category']
  year?: number
  version_label?: string
  summary?: string
  is_authoritative?: boolean
  body_md?: string
}

// 带鉴权的详情(带 token,返回 can_edit / body_md),用于判断是否显示编辑按钮、加载编辑器。
export async function fetchRuleAuthed(slug: string): Promise<RuleDetail | null> {
  const { get } = await import('@/utils/request')
  const res = await get(`rules/${encodeURIComponent(slug)}`)
  return res?.ok ? res.data : null
}

export async function updateRule(slug: string, payload: RuleEditPayload): Promise<{ ok: boolean; data?: RuleDetail; message?: string }> {
  const { put } = await import('@/utils/request')
  return put(`rules/${encodeURIComponent(slug)}`, payload)
}

export async function fetchRules(
  params: { q?: string; category?: string; domain?: string; game?: string } = {},
): Promise<RuleCard[]> {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.category) qs.set('category', params.category)
  if (params.domain) qs.set('domain', params.domain)
  if (params.game) qs.set('game', params.game)
  const res = await fetch(`${API}/rules?${qs.toString()}`)
  const json = await res.json()
  return json.ok ? json.rules : []
}

export async function fetchRule(slug: string): Promise<RuleDetail | null> {
  const res = await fetch(`${API}/rules/${encodeURIComponent(slug)}`)
  const json = await res.json()
  return json.ok ? json.data : null
}

export const CATEGORY_LABELS: Record<RuleCard['category'], string> = {
  official_national: '国家官方',
  world: '世界',
  provincial: '地方',
  institutional: '配套制度',
  event: '赛事规程',
}

export type GamesTree = Array<{
  domain: 'card' | 'ball'
  label: string
  games: Array<{ game: string; label: string; count: number }>
}>

export async function fetchGames(): Promise<GamesTree> {
  const res = await fetch(`${API}/rules/games`)
  const json = await res.json()
  return json.ok ? json.domains : []
}

export async function uploadRule(form: FormData): Promise<{ ok: boolean; slug?: string; message?: string }> {
  const { postForm } = await import('@/utils/request')
  return postForm('rules/upload', form)
}

export async function fetchMyRules(): Promise<Array<RuleCard & { status: string }>> {
  const { get } = await import('@/utils/request')
  const res = await get('users/rules')
  return res.ok ? res.rules : []
}

export async function fetchWechatArticle(slug: string): Promise<{ ok: boolean; html?: string }> {
  const res = await fetch(`${API}/rules/${encodeURIComponent(slug)}/wechat_article`)
  const json = await res.json()
  return json
}

export async function recordRuleView(slug: string): Promise<void> {
  try {
    await fetch(`${API}/rules/${encodeURIComponent(slug)}/view`, { method: 'POST' })
  } catch {
    // 静默失败，不影响阅读
  }
  const { track } = await import('@/utils/track')
  track('rule_view', { target: slug })
}
